import { FC, useEffect, useRef, useState } from 'react';
import { BsTrash } from 'react-icons/bs';
import { Moment } from 'moment';

import { ActionConfirmation, Box } from '@qwealth/qcore';

import InfoTag from 'components/common/SectionHeader/InfoTag';
import { BottomNoteCard, NoteCard, NoteCardContainer, StyledDeleteButton } from './NotesPreviewCardStyles';
import PreviewIcon from './PreviewIcon';
import KYCTagsDisplay from './KYCTagsDisplay';
import WealthboxConflictTag from './WealthboxConflictTag';
import PreviewDetailsSection from './PreviewDetailsSection';

type NotesPreviewCardType = 'QNote' | 'Email' | 'Task' | 'Call Log' | 'Event';

interface NotesPreviewCardProps {
  title: string;
  uuid: string;
  type: NotesPreviewCardType;
  lastUpdated: string;
  lastUpdatedBy: string;
  created: string;
  createdBy: string;
  meetingDate?: string;
  syncDate: string;
  isWealthbox?: boolean;
  isSignificantChange: boolean;
  isMeaningfulInteraction: boolean;
  isPortfolioReview: boolean;
  handleEdit: () => void;
  handleDelete: () => void;
  emailTo?: string;
  emailFrom?: string;
  eventStart?: Moment;
  eventEnd?: Moment;
  hasConflict?: boolean;
  setPreviousModalHidden?: React.Dispatch<React.SetStateAction<boolean>>;
  isCardSelected?: boolean;
}

const NotesPreviewCard: FC<NotesPreviewCardProps> = ({
  title,
  type,
  meetingDate,
  lastUpdated,
  lastUpdatedBy,
  created,
  createdBy,
  syncDate,
  isWealthbox,
  isSignificantChange,
  isMeaningfulInteraction,
  isPortfolioReview,
  handleEdit,
  handleDelete,
  emailTo,
  emailFrom,
  eventStart,
  eventEnd,
  hasConflict,
  setPreviousModalHidden,
  isCardSelected,
}): JSX.Element => {
  const hasKYCTag = isSignificantChange || isMeaningfulInteraction || isPortfolioReview;
  const noteCardRef = useRef<HTMLDivElement | null>(null);
  const [isSmallCard, setIsSmallCard] = useState<boolean>(true);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [bottomCardHeight, setBottomCardHeight] = useState<number>(0);

  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => setIsHovered(false);

  useEffect(() => {
    const noteCardWidth = noteCardRef.current?.getBoundingClientRect().width;
    const noteCardHeight = noteCardRef.current?.getBoundingClientRect().height;
    if (noteCardWidth) {
      setIsSmallCard(noteCardWidth < 706);
    }
    if (noteCardHeight) {
      setBottomCardHeight(noteCardHeight + 5);
    }
  }, [noteCardRef.current?.getBoundingClientRect().height]);

  const shortenLongTitle = (title: string): string => {
    return title.length > 20 ? title.slice(0, 17) + '...' : title;
  };

  return (
    <NoteCardContainer width="100%" marginBottom={hasConflict ? 'small' : 'none'}>
      {/* Top Card */}
      <NoteCard
        as="button"
        ref={noteCardRef}
        onClick={() => {
          if (!isDeleteModalOpen) {
            handleEdit();
          }
          setIsDeleteModalOpen(false);
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        isSelected={isCardSelected}
      >
        <Box width="100%" display="flex" flexDirection="row">
          <Box marginTop="small">
            <PreviewIcon type={type} isHovered={isHovered} eventStart={eventStart} />
          </Box>
          <Box width="100%" display="flex" flexDirection="column" marginLeft="small">
            <Box display="flex" flexDirection="row">
              <Box width="100%" display="flex" flexDirection="row" justifyContent="space-between">
                <Box display="flex" flexDirection="row" height="32px" alignItems="center" gap="default">
                  <Box display="flex" flexDirection="row" gap="default" marginLeft="small">
                    <Box fontWeight="bold">{type === 'QNote' ? shortenLongTitle(title) : title}</Box>
                  </Box>

                  {isWealthbox && <InfoTag tagTitle="Wealthbox" fontSize="small" />}
                  {!isSmallCard && (
                    <KYCTagsDisplay
                      isSignificantChange={isSignificantChange}
                      isMeaningfulInteraction={isMeaningfulInteraction}
                      isPortfolioReview={isPortfolioReview}
                    />
                  )}
                </Box>
                {!hasKYCTag && !hasConflict && (
                  <Box marginTop="small">
                    <ActionConfirmation
                      actionHandler={() => {
                        handleDelete();
                      }}
                      ActionButtonRenderer={openHandler => (
                        <StyledDeleteButton
                          onClick={(event: React.MouseEvent) => {
                            event.stopPropagation();
                            setIsDeleteModalOpen(true);
                            openHandler();
                          }}
                        >
                          <BsTrash size="22px" />
                        </StyledDeleteButton>
                      )}
                      target={`delete "${title}"`}
                      setPreviousModalHidden={setPreviousModalHidden}
                    />
                  </Box>
                )}
                {hasConflict && !isSmallCard && <WealthboxConflictTag />}
              </Box>
            </Box>

            {hasKYCTag && isSmallCard && (
              <KYCTagsDisplay
                isSignificantChange={isSignificantChange}
                isMeaningfulInteraction={isMeaningfulInteraction}
                isPortfolioReview={isPortfolioReview}
              />
            )}

            <PreviewDetailsSection
              isSmallCard={isSmallCard}
              type={type}
              createdBy={createdBy}
              created={created}
              emailTo={emailTo}
              emailFrom={emailFrom}
              syncDate={syncDate}
              eventStart={eventStart}
              eventEnd={eventEnd}
              meetingDate={meetingDate}
            />
          </Box>
        </Box>
      </NoteCard>
      {/* Bottom Card */}
      {hasConflict && <BottomNoteCard height={bottomCardHeight.toString() + 'px'} isHovered={isHovered} />}
    </NoteCardContainer>
  );
};

export default NotesPreviewCard;
